<template>
    <div
        class="relative"
        @mouseleave="trigger === 'hover' ? onHoverLeave() : null">
        <div
            ref="popoverTrigger"
            class="contents"
            @mouseenter="trigger === 'hover' ? onHoverEnter() : null"
            @mouseleave="onTriggerHoverLeave"
            @click="trigger === 'click' && !disabled ? togglePopover() : null">
            <slot name="trigger" />
        </div>

        <Transition
            v-if="!disabled"
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100 translate-y-0"
            leave-to-class="opacity-0">
            <div
                v-show="isOpen"
                class="absolute z-50 flex w-screen max-w-max transition-opacity p-2"
                :class="basePositioning"
                :style="offset">
                <div
                    ref="panel"
                    :class="popoverStyles">
                    <slot />
                </div>
            </div>
        </Transition>
    </div>
</template>

<script setup>
const popoverTrigger = ref(null)

const props = defineProps({
    size: {
        type: String,
        default: 'md',
        description: 'large: lg || medium: md || small: sm'
    },
    position: {
        type: String,
        default: 'center',
        description: 'center || left || right || top || bottom || top-right || top-left || bottom-right || bottom-left || hidden'
    },
    hidden: {
        type: Boolean,
        default: false,
        description: 'hide popover'
    },
    trigger: {
        type: String,
        default: 'hover',
        description: 'hover || click'
    },
    disabled: {
        type: Boolean,
        default: false,
        description: 'disable popover'
    },
    tooltip: {
        type: Boolean,
        default: false,
        description: 'enables tooltip mode, which closes the popover whenever the mouse leaves the trigger element'
    },
    contentStyles: {
        type: String,
        default: '',
        description: 'additional style classes for the popover content'
    }
})

const isOpen = ref(false)

function onHoverEnter() {
    if (props.disabled) return
    if (!isOpen.value) isOpen.value = true
}

function onHoverLeave() {
    if (props.disabled) return
    isOpen.value = false
}

function onTriggerHoverLeave() {
    if (props.disabled || !props.tooltip || !isOpen.value) return
    isOpen.value = false
}

function togglePopover() {
    isOpen.value = !isOpen.value
}

function handleClickOutside(event) {
    if (props.trigger === 'click' && isOpen.value &&
        popoverTrigger.value && !popoverTrigger.value.contains(event.target) &&
        panel.value && !panel.value.contains(event.target)) {
        isOpen.value = false
    }
}

onMounted(() => {
    if (props.trigger === 'click') {
        document.addEventListener('mousedown', handleClickOutside)
    }
})

onUnmounted(() => {
    document.removeEventListener('mousedown', handleClickOutside)
})

// panel + trigger positioning
const panel = ref(null)
const triggerEl = popoverTrigger

// Use immediate:false to prevent race conditions
const panelBoundingRect = useElementBounding(panel, {immediate: false})
const triggerBoundingRect = useElementBounding(triggerEl, {immediate: false})

const {width: windowWidth} = useWindowSize()

const yPosition = computed(() => {
    if (['top', 'top-left', 'top-right'].includes(props.position)) return 'top'
    if (['left', 'right'].includes(props.position)) return 'center'
    return 'bottom'
})

const xPosition = computed(() => {
    if (['left', 'top-left', 'bottom-left'].includes(props.position)) return 'left'
    if (['right', 'top-right', 'bottom-right'].includes(props.position)) return 'right'
    return 'center'
})

const offset = computed(() => {
    if (!triggerBoundingRect || !triggerBoundingRect.x) return ''

    const PADDING = 8
    const posX = triggerBoundingRect.x.value
    const rightX = posX + triggerBoundingRect.width.value
    const posY = triggerBoundingRect.y.value
    const bottomY = posY + triggerBoundingRect.height.value

    const leftOffset = posX < 0 ? Math.floor(posX) - PADDING : 0
    const rightOffset = rightX > windowWidth.value ? Math.floor(rightX - windowWidth.value) + PADDING : 0
    const topOffset = posY < 0 ? Math.floor(posY) - PADDING : 0
    const bottomOffset = bottomY > window.innerHeight ? Math.floor(bottomY - window.innerHeight) + PADDING : 0

    let transform = ''
    if (yPosition.value === 'top') transform += `translateY(calc(-100% - ${topOffset}px - ${bottomOffset}px)) `
    if (yPosition.value === 'center') transform += `translateY(calc(-50% - ${topOffset}px - ${bottomOffset}px)) `
    if (yPosition.value === 'bottom') transform += `translateY(calc(${topOffset}px - ${bottomOffset}px)) `
    if (xPosition.value === 'center') {
        // Center the popover over the trigger; account for panel width if available
        const panelHalf = panelBoundingRect?.width?.value ? Math.floor(panelBoundingRect.width.value / 2) : 0
        transform += `translateX(calc(-50% - ${leftOffset}px - ${rightOffset}px - ${panelHalf}px))`
    }
    if (xPosition.value === 'left') transform += `translateX(calc(-100% - ${leftOffset}px - ${rightOffset}px))`
    if (xPosition.value === 'right') transform += `translateX(calc(${leftOffset}px - ${rightOffset}px))`

    return transform ? ('transform: ' + transform + ';') : ''
})

const popoverStyles = computed(() => {
    if (props.contentStyles) {
        return props.contentStyles
    }
    const baseClasses = [
        'flex-auto',
        'rounded-xl',
        'bg-white',
        'dark:bg-gray-800',
        'px-4',
        'py-2',
        'text-sm',
        'shadow',
        'ring-1',
        'ring-gray-900/5',
        'dark:ring-gray-300',
    ]
    const sizeClasses = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg'
    }
    return [...baseClasses, sizeClasses[props.size] || sizeClasses.md].join(' ')
})

const basePositioning = computed(() => {
    let classes = ''
    if (yPosition.value === 'top') classes += 'top-0 '
    if (yPosition.value === 'center') classes += 'top-1/2 '

    if (yPosition.value === 'center') {
        if (xPosition.value === 'left') classes += 'left-0 '
        if (xPosition.value === 'center') classes += 'left-1/2 '
        if (xPosition.value === 'right') classes += 'left-full '
    } else {
        classes += 'left-1/2 '
    }

    return classes
})
</script>